import { useState, useMemo, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { ProductQuantityCalculator } from "../../utils/ProductQuantityCalculator";
import QuantityInput from "../../components/cart/QuantityInput";
import { formatCurrency } from "../../utils/formatters";
import ImageComingSoon from "../../components/common/ImageComingSoon";
import StoreOrderInfo from "../../components/common/StoreOrderInfo";
import authService from "../../services/authService";
import { useMedRecFilter } from "../../context/MedRecFilterContext";
import { appInsights } from "../../appInsights.js";
import { useStoreCredits } from "../../hooks/useStoreCredits";
import CheckoutCredits from "../../components/cart/CheckoutCredits";
import { byTypeOrder, resolveCreditPicks } from "../../utils/checkoutCredits";
import { creditLineLabel } from "../../services/creditService.js";

// Cannabis icon for Med/Rec badges
import cannabisIcon from "../../assets/icons/reccannabis.png";

const CartPage = () => {
  const {
    cartItems,
    clearCart,
    getCartOriginalSubtotal,
    getCartFeeTotal,
    getCartTotal,
    getCartSavings,
    brandTotals,
    creditableCartTotal,
    updateCartItemQuantity,
    removeFromCart,
    checkout,
    isLoading,
    isUpdatingCart,
    isCheckingOut,
    error,
    validationResults,
    hasValidationErrors,
    canUseCart,
  } = useCart();

  // Med/Rec filter context - only show badge when store supports both
  const { showFilter: showMedRecBadge } = useMedRecFilter();

  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState("");
  const [availableDeliveryDates, setAvailableDeliveryDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(true);
  const [specialNotes, setSpecialNotes] = useState("");
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [inventoryShortages, setInventoryShortages] = useState([]); // Track items with insufficient inventory
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth });
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // The credits the customer has chosen, in the order they ticked them (credits apply in that order).
  // Null until they change anything: until then the selection is the automatic one (below). Once they
  // tick or untick, only their choice counts — unticking a credit never pulls in another one, since they
  // may not want any credits on this order.
  const [chosenCreditOrder, setChosenCreditOrder] = useState(null);
  // What checkout actually applied, for the thank-you screen.
  const [appliedCredits, setAppliedCredits] = useState([]);

  const { enabled: creditsEnabled, availableCredits: storeCredits, stashHouseBrands } = useStoreCredits();
  const availableCredits = useMemo(() => (creditsEnabled ? storeCredits : []), [creditsEnabled, storeCredits]);
  const cartTotal = getCartTotal();
  const houseBrandIds = useMemo(() => stashHouseBrands.map((b) => b.brandId), [stashHouseBrands]);
  // The credits that can be used on this cart at all (on their own); the rest aren't listed. Brand
  // credits first, then Stash House, then Overpay.
  const usableCredits = useMemo(() => {
    const { maxFor } = resolveCreditPicks(availableCredits, {}, creditableCartTotal, brandTotals, houseBrandIds);
    return availableCredits.filter((c) => maxFor(c) > 0).sort(byTypeOrder);
  }, [availableCredits, creditableCartTotal, brandTotals, houseBrandIds]);
  // The automatic selection: the credits that would apply if all were used, Brand credits first — only
  // those that actually take something off this cart.
  const autoCreditOrder = useMemo(() => {
    const all = Object.fromEntries(usableCredits.map((c) => [c.id, c.remainingAmount]));
    const { amounts } = resolveCreditPicks(usableCredits, all, creditableCartTotal, brandTotals, houseBrandIds);
    return usableCredits.filter((c) => amounts[c.id] > 0).map((c) => c.id);
  }, [usableCredits, creditableCartTotal, brandTotals, houseBrandIds]);
  const selectedCreditOrder = chosenCreditOrder ?? autoCreditOrder;

  // The selected credits in the order picked, then the rest. Each selected credit asks for its whole
  // balance and gets what's left after the ones picked before it.
  const orderedCredits = useMemo(() => {
    const byId = new Map(usableCredits.map((c) => [c.id, c]));
    const selected = selectedCreditOrder.map((id) => byId.get(id)).filter(Boolean);
    return [...selected, ...usableCredits.filter((c) => !selectedCreditOrder.includes(c.id))];
  }, [usableCredits, selectedCreditOrder]);
  const selectedCreditIds = useMemo(() => new Set(selectedCreditOrder), [selectedCreditOrder]);
  const {
    amounts: creditAmounts,
    maxFor: creditMaxFor,
    total: creditTotal,
  } = useMemo(() => {
    const wanted = Object.fromEntries(
      orderedCredits.filter((c) => selectedCreditIds.has(c.id)).map((c) => [c.id, c.remainingAmount]),
    );
    return resolveCreditPicks(orderedCredits, wanted, creditableCartTotal, brandTotals, houseBrandIds);
  }, [orderedCredits, selectedCreditIds, creditableCartTotal, brandTotals, houseBrandIds]);

  // Ticking adds a credit after the ones already picked; unticking drops it.
  const toggleCredit = (credit, checked) =>
    setChosenCreditOrder(
      checked
        ? [...selectedCreditOrder.filter((id) => id !== credit.id), credit.id]
        : selectedCreditOrder.filter((id) => id !== credit.id),
    );

  // Track window size for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get current store/client info for route days calculation
  const currentStore = authService.getCurrentStore();

  // Calculate available delivery dates based on route days, frequency, and schedule
  const calculateNextDeliveryDates = useCallback((route, companySettings = {}) => {
    // Helper: Get week of month (1-4) for a given date
    const getWeekOfMonth = (date) => {
      const dayOfMonth = date.getDate();
      if (dayOfMonth <= 7) return 1;
      if (dayOfMonth <= 14) return 2;
      if (dayOfMonth <= 21) return 3;
      return 4;
    };

    // Helper: Parse schedule name to get valid weeks
    const getValidWeeksFromScheduleName = (scheduleName) => {
      if (!scheduleName) return [1, 2, 3, 4]; // All weeks if no schedule

      const name = scheduleName.toLowerCase();

      // Bi-weekly schedules
      if (name.includes('1st') && name.includes('3rd')) return [1, 3];
      if (name.includes('2nd') && name.includes('4th')) return [2, 4];

      // Monthly schedules - single week
      if (name.includes('1st')) return [1];
      if (name.includes('2nd')) return [2];
      if (name.includes('3rd')) return [3];
      if (name.includes('4th')) return [4];

      return [1, 2, 3, 4]; // Default to all weeks
    };

    // Helper: Check if a date matches the route's frequency and schedule
    const isDateValidForSchedule = (date, frequencyId, scheduleName) => {
      // Weekly (frequencyId = 1) or no frequency - all dates valid
      if (!frequencyId || frequencyId === 1) return true;

      const weekOfMonth = getWeekOfMonth(date);
      const validWeeks = getValidWeeksFromScheduleName(scheduleName);

      return validWeeks.includes(weekOfMonth);
    };

    // Support both old format (routeDays array) and new format (route object)
    const routeDays = Array.isArray(route) ? route : route?.routeDays;
    const frequencyId = Array.isArray(route) ? null : route?.optRouteFrequency?.id;
    const scheduleName = Array.isArray(route) ? null : route?.optRouteSchedule?.name;

    if (!routeDays || routeDays.length === 0) return [];

    // Build per-day cutoff lookup from OrderCutoffs; fall back to 0 if no cutoff configured
    const orderCutoffs = companySettings.orderCutoffs || [];
    const cutoffByDay = {};
    orderCutoffs.forEach(oc => {
      cutoffByDay[oc.optDayID] = oc.hours;
    });
    const timezone = companySettings.optTimeZone?.timeZone || 'America/Chicago';

    // Get current time in company's timezone
    const now = new Date();
    let companyNow;
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const parts = formatter.formatToParts(now);
      const dateParts = {};
      parts.forEach(part => {
        dateParts[part.type] = part.value;
      });
      companyNow = new Date(
        `${dateParts.year}-${dateParts.month}-${dateParts.day}T${dateParts.hour}:${dateParts.minute}:00`
      );
    } catch (e) {
      console.warn('Invalid timezone, falling back to local time:', timezone);
      companyNow = now;
    }

    const availableDates = [];
    const routeDayIds = routeDays.map((rd) => rd.optDayID);

    // Check up to 35 days out to find valid delivery dates (needed for monthly routes)
    for (let i = 0; i < 35; i++) {
      const checkDate = new Date(companyNow);
      checkDate.setDate(companyNow.getDate() + i);
      checkDate.setHours(0, 0, 0, 0);

      const dayOfWeek = checkDate.getDay() + 1; // 1=Sunday, 2=Monday, etc.

      // Check if this day of week is a route day AND matches the frequency/schedule
      if (routeDayIds.includes(dayOfWeek) && isDateValidForSchedule(checkDate, frequencyId, scheduleName)) {
        // Calculate cutoff deadline using per-day cutoff hours:
        // Cutoff hours means orders must be placed X hours before the START of the delivery day
        const cutoffHours = cutoffByDay[dayOfWeek] ?? 0;
        const deliveryDayStart = new Date(checkDate); // midnight of delivery day
        const cutoffDeadline = new Date(deliveryDayStart.getTime() - (cutoffHours * 60 * 60 * 1000));

        if (companyNow < cutoffDeadline) {
          const routeDay = routeDays.find((rd) => rd.optDayID === dayOfWeek);
          availableDates.push({
            date: checkDate.toISOString().split("T")[0],
            dayName: routeDay?.optDay?.name || getDayName(dayOfWeek),
            shortName: routeDay?.optDay?.shortName || getDayShortName(dayOfWeek),
            formatted: checkDate.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            }),
          });

          if (availableDates.length >= 2) break; // Get up to 2 dates
        }
      }
    }

    return availableDates;
  }, []);

  // Helper functions for day names
  const getDayName = (dayId) => {
    const days = ['', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayId] || '';
  };

  const getDayShortName = (dayId) => {
    const days = ['', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[dayId] || '';
  };

  // Load delivery dates from store/client route
  useEffect(() => {
    const loadDeliveryDates = async () => {
      setIsLoadingDates(true);
      try {
        // Get route information from the current store
        if (currentStore?.route?.routeDays && currentStore.route.routeDays.length > 0) {
          const companySettings = {
            orderCutoffs: currentStore.company?.orderCutoffs || [],
            optTimeZone: currentStore.company?.optTimeZone
          };
          const dates = calculateNextDeliveryDates(currentStore.route, companySettings);
          setAvailableDeliveryDates(dates);
          if (dates.length > 0) {
            setSelectedDeliveryDate(dates[0].date);
          }
        } else {
          // No route configured - show empty state
          setAvailableDeliveryDates([]);
        }
      } catch (error) {
        console.error("Error loading delivery dates:", error);
        setAvailableDeliveryDates([]);
      } finally {
        setIsLoadingDates(false);
      }
    };

    if (canUseCart) {
      loadDeliveryDates();
    }
  }, [currentStore, canUseCart, calculateNextDeliveryDates]);

  const handleRemoveItem = async (item) => {
    if (appInsights) {
      appInsights.trackEvent({ name: 'CartItemDeleted' }, {
        productId: String(item.productSkuVariantID || item.id || ''),
        productName: item.productName || '',
        brandName: item.brandName || '',
        unitQuantity: item.unitQuantity,
        source: 'cartPage',
      });
    }
    try {
      await removeFromCart(item.id);
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  const handleUpdateQuantity = async (item, newQuantity) => {
    try {
      await updateCartItemQuantity(item.id, newQuantity);

      // Check if this item had an inventory shortage and the new quantity satisfies it
      const shortage = inventoryShortages.find(
        s => s.productSkuVariantId === item.productSkuVariantID
      );
      if (shortage && newQuantity <= shortage.availableQuantity) {
        // Clear this specific shortage
        setInventoryShortages(prev =>
          prev.filter(s => s.productSkuVariantId !== item.productSkuVariantID)
        );
        // If no more shortages, clear the error message too
        if (inventoryShortages.length === 1) {
          setCheckoutError(null);
        }
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleClearCart = () => setShowClearConfirm(true);

  const confirmClearCart = async () => {
    setShowClearConfirm(false);
    try {
      await clearCart();
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  // Checkout errors show at the top of the page, far above Place Order: scroll up so they're seen.
  const showCheckoutError = (message) => {
    setCheckoutError(message);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCheckout = async () => {
    if (!selectedDeliveryDate) {
      showCheckoutError("Please select a delivery date");
      return;
    }

    if (cartItems.length === 0) {
      showCheckoutError("Your cart is empty");
      return;
    }

    if (hasValidationErrors) {
      showCheckoutError("Please resolve inventory issues before checkout");
      return;
    }

    // Recalculate delivery dates to ensure the selected date is still valid
    // (handles stale browser tabs where the customer sat on the page for days)
    if (currentStore?.route?.routeDays && currentStore.route.routeDays.length > 0) {
      const companySettings = {
        orderCutoffs: currentStore.company?.orderCutoffs || [],
        optTimeZone: currentStore.company?.optTimeZone
      };
      const freshDates = calculateNextDeliveryDates(currentStore.route, companySettings);
      const stillValid = freshDates.some(d => d.date === selectedDeliveryDate);

      if (!stillValid) {
        setAvailableDeliveryDates(freshDates);
        setSelectedDeliveryDate(freshDates.length > 0 ? freshDates[0].date : null);
        showCheckoutError("Your selected delivery date is no longer available. Please select a new delivery date and try again.");
        return;
      }
    }

    setCheckoutError(null);
    setInventoryShortages([]); // Clear previous inventory shortages

    try {
      const result = await checkout({
        requestedDeliveryDate: selectedDeliveryDate,
        specialNotes: specialNotes,
        // In the order picked — the server applies them in this order.
        credits: selectedCreditOrder
          .filter((id) => creditAmounts[id] > 0)
          .map((id) => ({ clientCreditId: id, amount: creditAmounts[id] })),
      });
      setAppliedCredits(result?.appliedCredits || []);
      setChosenCreditOrder(null);
      setCheckoutSuccess(true);
      // Stay on success page - user can navigate via buttons
    } catch (error) {
      console.error("Checkout error:", error);

      // Check if this is an inventory error with details
      if (error.isInventoryError && error.inventoryDetails) {
        setInventoryShortages(error.inventoryDetails);
        showCheckoutError("Some items have insufficient inventory. Please adjust quantities below.");
      } else {
        setInventoryShortages([]);
        showCheckoutError(error.message || "Failed to place order. Please try again.");
      }
    }
  };

  // Transform cart items for display
  const displayItems = useMemo(() => {
    return cartItems.map((item) => {
      // Create a product object for the calculator - include pricing fields
      const productData = {
        id: item.productSkuVariantID,
        unitsPerCase: item.productSkuVariant?.unitsPerCase || 1,
        optSellByTypeID: item.productSkuVariant?.optSellByTypeID || 1,
        unitAvailable: item.productSkuVariant?.unitAvailable || 0,
        // Pricing fields - check both item level (from cart) and productSkuVariant level.
        // Prefer the product's current frontline price (productSkuVariant) over the stored
        // line price: item.unitPrice already has any discount baked in at add-time, which would
        // hide the strikethrough. Letting the calculator derive the discount/special off the
        // frontline keeps the effective price the same while restoring the crossed-out original.
        unitPrice: item.productSkuVariant?.unitPrice || item.unitPrice || 0,
        discountUnitPrice: item.discountUnitPrice || item.productSkuVariant?.discountUnitPrice || null,
        singleUnitFee: item.singleUnitFee || item.productSkuVariant?.singleUnitFee || 0,
        productSkuVariantMOQPricing: item.productSkuVariantMOQPricing || item.productSkuVariant?.productSkuVariantMOQPricing || [],
      };

      const calculator = new ProductQuantityCalculator(productData);
      const displayQuantity = calculator.unitsToDisplay(item.unitQuantity || 0);

      const validation = validationResults?.find((v) => v.itemId === item.id);

      const getImageUrl = (item) => {
        if (
          item.productSkuVariant?.images &&
          item.productSkuVariant.images.length > 0
        ) {
          return item.productSkuVariant.images[0].fullPath;
        }

        if (item.productSkuVariant?.productSKUVariantImages?.length > 0) {
          const firstImage = item.productSkuVariant.productSKUVariantImages[0];
          if (firstImage?.image?.fullPath) {
            return `https://stashhouseimages.blob.core.windows.net/${firstImage.image.fullPath}`;
          }
        }

        return null;
      };

      // Check if this item has an inventory shortage from checkout error
      const inventoryShortage = inventoryShortages.find(
        shortage => shortage.productSkuVariantId === item.productSkuVariantID
      );

      return {
        ...item,
        calculator,
        displayQuantity,
        validation,
        hasValidationError: validation && !validation.isValid,
        productName: item.productSkuVariant?.name || "Unknown Product",
        brandName: item.productSkuVariant?.brand?.name,
        flavor: item.productSkuVariant?.flavor,
        imageUrl: getImageUrl(item),
        unitsPerCase: productData.unitsPerCase,
        optSellByTypeID: productData.optSellByTypeID,
        canSellByUnit: calculator.canSellByUnit,
        canSellByCase: calculator.canSellByCase,
        unitAvailable: productData.unitAvailable,
        singleUnitFee: productData.singleUnitFee,
        // Inventory shortage info from checkout error
        inventoryShortage: inventoryShortage || null,
        hasInventoryShortage: !!inventoryShortage,
        // Med/Rec status
        isMed: item.productSkuVariant?.isMed || false,
        isRec: item.productSkuVariant?.isRec || false,
      };
    });
  }, [cartItems, validationResults, inventoryShortages]);

  // Show message if user doesn't have store selected
  if (!canUseCart) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="mb-4">
              <i className="bi bi-shop display-1 text-muted"></i>
            </div>
            <h2 className="mb-3">Select a Store First</h2>
            <p className="text-muted mb-4">
              Please select a store before viewing your cart and checking out.
            </p>
            <Link to="/account" className="btn btn-primary btn-lg">
              Select Store
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (checkoutSuccess) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5 px-4">
                <div className="mb-4">
                  <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '100px', height: '100px' }}>
                    <i className="bi bi-check-lg display-3 text-success"></i>
                  </div>
                </div>
                <h2 className="mb-2">Thank You!</h2>
                <h4 className="text-muted mb-4">Your order has been placed successfully</h4>
                <p className="text-muted mb-4">
                  We've received your order and will begin processing it shortly.
                  You'll receive a confirmation email with your order details.
                </p>
                {appliedCredits.length > 0 && (
                  <div className="border rounded text-start p-3 mb-4">
                    <div className="fw-semibold mb-2">
                      <i className="bi bi-wallet2 me-2 text-stash-accent"></i>
                      Credits applied
                    </div>
                    {appliedCredits.map((row, i) => (
                      <div key={i} className="d-flex justify-content-between">
                        <span>
                          {creditLineLabel(row)} credit
                          {new Set(appliedCredits.map((r) => r.orderId)).size > 1 && (
                            <small className="text-muted"> · order #{row.orderId}</small>
                          )}
                        </span>
                        <span className="text-danger">({formatCurrency(row.amount)})</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/orders" className="btn btn-primary btn-lg">
                    <i className="bi bi-box-seam me-2"></i>
                    View Orders
                  </Link>
                  <Link to="/categories" className="btn btn-outline-secondary btn-lg">
                    <i className="bi bi-cart-plus me-2"></i>
                    Place Another Order
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="mb-1">Checkout</h1>
              <p className="text-muted mb-0">
                Review your order and select a delivery date
              </p>
            </div>
            <div className="d-flex align-items-center gap-3">
              {cartItems.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link text-danger p-0 border-0 bg-transparent text-decoration-none d-none d-sm-inline-block"
                  onClick={handleClearCart}
                  disabled={isLoading}
                >
                  <i className="bi bi-trash me-1"></i>
                  Clear All
                </button>
              )}
              <Link to="/categories" className="btn btn-outline-secondary">
                <i className="bi bi-arrow-left me-2"></i>
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display - Hide inventory error alert when all shortages are resolved */}
      {(() => {
        // Check if error is inventory-related (case-insensitive)
        const errorMessage = checkoutError || (typeof error === "string" ? error : error?.message || "");
        const isInventoryError = errorMessage.toLowerCase().includes("insufficient inventory");

        // Hide inventory errors when all shortages are resolved
        if (isInventoryError && inventoryShortages.length === 0) {
          return false;
        }

        return error || checkoutError;
      })() && (
        <div className="alert alert-danger border-danger text-danger mb-4" role="alert">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>
                  {checkoutError || (typeof error === "string" ? error : error?.message || "An error occurred")}
                </strong>
              </div>
              {/* Show detailed inventory shortage information */}
              {inventoryShortages.length > 0 && (
                <ul className="mb-0 ps-4 small">
                  {inventoryShortages.map((shortage, index) => {
                    // Find the product name from cart items
                    const cartItem = cartItems.find(
                      item => item.productSkuVariantID === shortage.productSkuVariantId
                    );
                    const productName = cartItem?.productSkuVariant?.name || `Product #${shortage.productSkuVariantId}`;
                    return (
                      <li key={index} className="mb-1">
                        <strong>{productName}</strong>: Only {shortage.availableQuantity} available (you requested {shortage.requestedQuantity})
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <button
              type="button"
              className="btn-close btn-close-sm ms-3"
              onClick={() => {
                setCheckoutError(null);
                setInventoryShortages([]);
              }}
            ></button>
          </div>
        </div>
      )}

      {cartItems.length === 0 ? (
        <div className="row justify-content-center">
          <div className="col-md-6 text-center py-5">
            <div className="mb-4">
              <i className="bi bi-cart3 display-1 text-muted"></i>
            </div>
            <h3 className="text-muted mb-3">Your cart is empty</h3>
            <p className="text-muted mb-4">Add some products to get started</p>
            <Link to="/categories" className="btn btn-primary btn-lg">
              Browse Products
            </Link>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* Cart Items */}
          <div className="col-lg-8">
            <div className="card border-dark mb-4 checkout-cart">
              <div className="card-header bg-stash-accent d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0 text-white">
                  Cart Items ({cartItems.length})
                </h5>
                <div className="d-flex align-items-center gap-2">
                  {isUpdatingCart && (
                    <div className="spinner-border spinner-border-sm text-white" role="status">
                      <span className="visually-hidden">Updating...</span>
                    </div>
                  )}
                  {cartItems.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light d-sm-none"
                      onClick={handleClearCart}
                      disabled={isLoading}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body p-0">
                {/* Global Inventory Warning */}
                {hasValidationErrors && (
                  <div className="alert alert-danger small py-2 m-3 mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Some items have inventory issues. Please review quantities below.
                  </div>
                )}

                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead className="table-light d-none">
                      <tr>
                        <th style={{ width: "60px" }}>Image</th>
                        <th>Product</th>
                        <th>Price / Quantity</th>
                        <th style={{ width: "120px" }}>Total</th>
                        <th style={{ width: "60px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayItems.map((item) => (
                        <CartItemRow
                          key={item.id}
                          item={item}
                          onUpdateQuantity={handleUpdateQuantity}
                          onRemoveItem={handleRemoveItem}
                          isUpdating={isUpdatingCart}
                          showMedRecBadge={showMedRecBadge}
                          isMobile={windowSize.width <= 768}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary & Delivery Date */}
          <div className="col-lg-4">
            {/* Delivery Date Selection */}
           
            <div className="card border-dark mb-4">
              <div className="card-header bg-stash-accent py-3">
                <h5 className="mb-0 text-white">
                  <i className="bi bi-calendar-event me-2"></i>
                  Select Delivery Date
                </h5>
              </div>
              <div className="card-body">
                {isLoadingDates ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading dates...</span>
                    </div>
                    <p className="text-muted small mt-2 mb-0">Loading available dates...</p>
                  </div>
                ) : availableDeliveryDates.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {availableDeliveryDates.map((dateOption) => (
                      <label
                        key={dateOption.date}
                        className={`btn ${
                          selectedDeliveryDate === dateOption.date
                            ? "btn-primary"
                            : "btn-outline-primary"
                        } text-start d-flex justify-content-between align-items-center py-3`}
                        style={{ cursor: 'pointer' }}
                      >
                        <input
                          type="radio"
                          name="deliveryDate"
                          value={dateOption.date}
                          checked={selectedDeliveryDate === dateOption.date}
                          onChange={(e) => setSelectedDeliveryDate(e.target.value)}
                          className="d-none"
                        />
                        <span>
                          <strong>{dateOption.dayName}</strong>
                          <br />
                          <small className={selectedDeliveryDate === dateOption.date ? "text-white-50" : "text-muted"}>
                            {dateOption.formatted}
                          </small>
                        </span>
                        {selectedDeliveryDate === dateOption.date && (
                          <i className="bi bi-check-circle-fill fs-5"></i>
                        )}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bi bi-calendar-x display-4 text-muted d-block mb-3"></i>
                    <h6 className="text-muted mb-2">No Delivery Dates Available</h6>
                    <p className="text-muted small mb-0">
                      Your account does not have a delivery route configured.
                      Please contact your sales representative.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Special Notes */}
            <div className="card border-dark mb-4">
               <div className="card-header bg-stash-accent py-3">
                <h5 className="mb-0 text-white">
                  <i className="bi bi-chat-text me-2"></i>
                  Special Instructions
                </h5>
              </div>
              <div className="card-body">
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Delivery instructions, gate codes, special requests..."
                  value={specialNotes}
                  onChange={(e) => setSpecialNotes(e.target.value)}
                ></textarea>
              </div>
            </div>

            {/* Order Summary */}
            <div className="card border-dark">
              <div className="card-header bg-stash-accent py-3">
                <h5 className="mb-0 text-white">
                  <i className="bi bi-receipt me-2"></i>
                  Order Summary
                </h5>
              </div>
              <div className="card-body">
                {/* Payment Info */}
                {(currentStore?.optPaymentType || currentStore?.optPaymentTerm) && (
                  <div className="mb-3 pb-3 border-bottom">
                    {currentStore?.optPaymentType && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">
                          <i className="bi bi-credit-card me-2"></i>
                          Payment Method
                        </span>
                        <span className="fw-medium">
                          {currentStore.optPaymentType.displayName || currentStore.optPaymentType.name}
                        </span>
                      </div>
                    )}
                    {currentStore?.optPaymentTerm && (
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">
                          <i className="bi bi-calendar3 me-2"></i>
                          Payment Terms
                        </span>
                        <span className="fw-medium">
                          {currentStore.optPaymentTerm.displayName || currentStore.optPaymentTerm.name}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <table className="table table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td>Subtotal</td>
                      <td className="text-end fw-medium">
                        {formatCurrency(getCartOriginalSubtotal())}
                      </td>
                    </tr>
                    <tr>
                      <td>Fees</td>
                      <td className="text-end fw-medium">
                        {formatCurrency(getCartFeeTotal())}
                      </td>
                    </tr>
                    <tr>
                      <td>Delivery</td>
                      <td className="text-end fw-medium text-success">FREE</td>
                    </tr>
                    {getCartSavings() > 0 && (
                      <tr>
                        <td className="text-success">
                          <i className="bi bi-tag-fill me-1"></i>
                          Your Savings
                        </td>
                        <td className="text-end fw-medium text-success">
                          -{formatCurrency(getCartSavings())}
                        </td>
                      </tr>
                    )}
                    {/* Credits: applied automatically just above the Order Total; untick to keep one for later */}
                    {usableCredits.length > 0 && cartItems.length > 0 && (
                      <tr>
                        <td colSpan={2}>
                          <CheckoutCredits
                            credits={orderedCredits}
                            selectedIds={selectedCreditIds}
                            amounts={creditAmounts}
                            maxFor={creditMaxFor}
                            onToggle={toggleCredit}
                            disabled={isCheckingOut}
                          />
                        </td>
                      </tr>
                    )}
                    <tr className="border-top">
                      <th>Order Total</th>
                      <td className="text-end">
                        <span className="fw-bold text-stash-accent fs-4">
                          {formatCurrency(cartTotal - creditTotal)}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="d-grid gap-2 mt-4">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleCheckout}
                    disabled={
                      isLoading ||
                      isCheckingOut ||
                      !selectedDeliveryDate ||
                      cartItems.length === 0 ||
                      hasValidationErrors ||
                      availableDeliveryDates.length === 0
                    }
                  >
                    {isCheckingOut ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-bag-check me-2"></i>
                        Place Order
                      </>
                    )}
                  </button>
                  <Link to="/categories" className="btn btn-outline-secondary">
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>

            {/* Store Order Info - Sales Rep, Cutoff, Delivery Days */}
            <div className="mt-4">
              <StoreOrderInfo variant="default" />
            </div>
          </div>
        </div>
      )}

      {/* Clear cart confirmation */}
      {showClearConfirm && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ zIndex: 1065 }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                    Clear cart?
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowClearConfirm(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Are you sure you want to remove all items from your cart? This
                    can&apos;t be undone.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowClearConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={confirmClearCart}>
                    Clear Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
        </>
      )}
    </div>
  );
};

// Cart Item Row Component
const CartItemRow = ({ item, onUpdateQuantity, onRemoveItem, isUpdating, showMedRecBadge, isMobile }) => {
  const handleCaseChange = (newCases) => {
    const newTotal = item.calculator.displayToUnits(
      newCases,
      item.displayQuantity.units
    );
    onUpdateQuantity(item, newTotal);
  };

  const handleUnitChange = (newUnits) => {
    const newTotal = item.calculator.displayToUnits(
      item.displayQuantity.cases,
      newUnits
    );
    onUpdateQuantity(item, newTotal);
  };

  // Calculate MOQ-aware pricing using the calculator
  const priceInfo = useMemo(() => {
    if (item.calculator) {
      return item.calculator.calculatePrice(item.unitQuantity || 0);
    }
    // Fallback calculation if no calculator
    const lineSubtotal = (item.unitQuantity || 0) * (item.unitPrice || 0);
    const lineFees = (item.unitQuantity || 0) < item.unitsPerCase
      ? parseFloat(item.singleUnitFee || 0) * (item.unitQuantity || 0)
      : 0;
    return {
      unitPrice: item.unitPrice || 0,
      originalUnitPrice: item.unitPrice || 0,
      totalPrice: lineSubtotal,
      fees: lineFees,
      grandTotal: lineSubtotal + lineFees,
      hasSavings: false,
      isMOQPrice: false,
      savingsPercent: 0,
      totalSavings: 0
    };
  }, [item.calculator, item.unitQuantity, item.unitPrice, item.unitsPerCase, item.singleUnitFee]);

  const lineFees = priceInfo.fees;
  const lineTotal = priceInfo.grandTotal;
  const effectiveUnitPrice = priceInfo.unitPrice;

  return (
    <tr
      className={`cart-item-row ${
        item.hasValidationError ? "table-warning" : ""
      } ${item.hasInventoryShortage ? "table-danger" : ""}`}
    >
      {/* Product Image */}
      <td>
        <div
          className="ratio ratio-1x1 rounded overflow-hidden bg-light text-center mx-auto border border-dark"
          style={{ width: "64px", height: "64px" }}
        >
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.productName}
              className="object-fit-cover mx-auto"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ImageComingSoon style={{ minHeight: "48px" }} />
          )}
        </div>
      </td>

      {/* Product Info */}
      <td>
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <strong className="text-truncate" style={{ maxWidth: "100%" }}>
              {item.productName}
            </strong>
            {/* Med/Rec badge - only show when store supports both types */}
            {showMedRecBadge && (item.isMed || item.isRec) && (
              <span
                className={`badge rounded-pill d-inline-flex align-items-center ${
                  item.isMed ? 'bg-success' : 'bg-purple'
                }`}
                title={item.isMed ? 'Medical Product' : 'Recreational Product'}
                style={{ fontSize: '0.65rem' }}
              >
                <img src={cannabisIcon} alt="" className="med-rec-badge-icon me-1" />
                {item.isMed ? 'Med' : 'Rec'}
              </span>
            )}
          </div>
          {item.brandName && (
            <small className="text-primary d-block">{item.brandName}</small>
          )}
          {item.flavor && (
            <small className="text-muted d-block">{item.flavor}</small>
          )}
          <small className="text-muted d-block">
            {item.unitsPerCase} {item.calculator?.unitLabelPlural?.toLowerCase() || 'units'} / {item.calculator?.caseLabel?.toLowerCase() || 'case'}
          </small>
          {item.hasValidationError && (
            <small className="text-warning d-block">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {item.validation.message}
            </small>
          )}
          {item.hasInventoryShortage && (
            <small className="text-danger d-block fw-bold">
              <i className="bi bi-exclamation-circle me-1"></i>
              Only {item.inventoryShortage.availableQuantity} {item.calculator?.unitLabelPlural?.toLowerCase() || 'units'} available
            </small>
          )}
        </div>
      </td>

      {/* Price + Quantity — single unified cell */}
      <td>
        <div className="d-flex flex-column gap-2 mx-auto mx-md-0">

          {/* === Cases Group === */}
          {item.canSellByCase && (
            <div>
              <div className={`d-flex ${isMobile ? 'flex-column align-items-center' : ''} gap-1`}>
                {/* Case price metadata */}
                <div className={isMobile ? 'mb-1' : 'text-end pe-2'} style={{minWidth: isMobile ? 'auto' : '180px'}}>
                  <small className="text-stash-accent" style={{lineHeight: 1}}>
                    {priceInfo.hasSavings && (
                      <span className="text-decoration-line-through text-muted me-1">
                        {formatCurrency(priceInfo.originalUnitPrice * item.unitsPerCase)}
                      </span>
                    )}
                    <strong>{formatCurrency(effectiveUnitPrice * item.unitsPerCase)}</strong> / {item.calculator?.caseLabel?.toLowerCase() || 'case'}
                    <span className="text-black-50 d-block" style={{fontSize: '0.75em', lineHeight: 1}}>({item.unitsPerCase} {item.calculator?.unitLabelPlural?.toLowerCase() || 'units'} / {item.calculator?.caseLabel?.toLowerCase() || 'case'})</span>
                  </small>
                </div>
                {/* Case input + label */}
                <QuantityInput
                  value={item.displayQuantity.cases}
                  onChange={handleCaseChange}
                  quantityType="cases"
                  product={{
                    id: item.productSkuVariantID,
                    unitsPerCase: item.unitsPerCase,
                    optSellByTypeID: item.optSellByTypeID,
                    unitAvailable: item.unitAvailable,
                  }}
                  currentUnitQuantity={item.displayQuantity.units}
                  size="sm"
                  disabled={isUpdating}
                  isUpdating={isUpdating}
                  label={item.calculator?.caseLabelPlural?.toLowerCase() || 'cases'}
                />
              </div>
            </div>
          )}

          {/* === Units Group === */}
          {item.canSellByUnit && item.unitsPerCase > 1 && (
            <div>
              <div className={`d-flex ${isMobile ? 'flex-column align-items-center' : ''} gap-1`}>
                {/* Unit price metadata */}
                <div className={isMobile ? 'mb-1' : 'text-end pe-2'} style={{minWidth: isMobile ? 'auto' : '180px'}}>
                  <small className="text-stash-accent" style={{lineHeight: 1}}>
                    {priceInfo.hasSavings && (
                      <span className="text-decoration-line-through text-muted me-1">
                        {formatCurrency(priceInfo.originalUnitPrice)}
                      </span>
                    )}
                    <strong>{formatCurrency(effectiveUnitPrice)}</strong> / {item.calculator?.unitLabel?.toLowerCase() || 'unit'}
                    {parseFloat(item.singleUnitFee || 0) > 0 && (
                      <span className="text-black-50 d-block" style={{fontSize: '0.75em', lineHeight: 1}}>(+{formatCurrency(parseFloat(item.singleUnitFee))} / {item.calculator?.unitLabel?.toLowerCase() || 'unit'} fee)</span>
                    )}
                  </small>
                </div>
                {/* Unit input + label */}
                <QuantityInput
                  value={item.displayQuantity.units}
                  onChange={handleUnitChange}
                  quantityType="units"
                  product={{
                    id: item.productSkuVariantID,
                    unitsPerCase: item.unitsPerCase,
                    optSellByTypeID: item.optSellByTypeID,
                    unitAvailable: item.unitAvailable,
                  }}
                  currentCaseQuantity={item.displayQuantity.cases}
                  size="sm"
                  disabled={isUpdating}
                  isUpdating={isUpdating}
                  label={item.calculator?.unitLabelPlural?.toLowerCase() || 'units'}
                />
              </div>
            </div>
          )}

          {priceInfo.isMOQPrice && (
            <span className="badge bg-success" style={{fontSize: '0.65rem'}}>
              Volume Pricing
            </span>
          )}
        </div>
      </td>

      {/* Line Total */}
      <td>
        <div className="text-end">
          {lineFees > 0 && (
            <small className="text-black-50 d-block">
              (incl. {formatCurrency(lineFees)} fees)
            </small>
          )}
          <small className="text-muted d-block">
            {formatCurrency(effectiveUnitPrice)} × {item.unitQuantity}
          </small>
          <strong className="text-stash-accent">{formatCurrency(lineTotal)}</strong>
          {priceInfo.hasSavings && (
            <small className="text-success d-block">
              Save {priceInfo.savingsPercent}%
            </small>
          )}
        </div>
      </td>

      {/* Actions */}
      <td className="text-center">
        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          onClick={() => onRemoveItem(item)}
          disabled={isUpdating}
          title="Remove from cart"
        >
          <i className="bi bi-trash"></i>
        </button>
      </td>
    </tr>
  );
};

export default CartPage;